/**
 * Where each bead sits inside a pit, and what colour it is. Pure and deterministic, so a
 * pit with 5 beads always looks the same, and adding a 6th never shuffles the first 5.
 */

/** Glass-bead colours: [main, deep] pairs for the radial gradient. */
export const BEAD_COLORS: readonly [string, string][] = [
  ['#4f8fe0', '#1f4f9a'], // blue
  ['#e2574c', '#8f2219'], // red
  ['#f2b134', '#9c6510'], // amber
  ['#4cb782', '#1d6b45'], // green
  ['#a874d8', '#5b2f8a'], // purple
  ['#3fc1c9', '#18737a'], // teal
  ['#f07fae', '#9a3462'], // pink
  ['#f4efe4', '#a99f8c'], // pearl
];

function hash(a: number, b: number): number {
  let h = (a * 374761393 + b * 668265263) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

export interface BeadSpot {
  /** Percent of the pit's width / height, centre of the bead. */
  x: number;
  y: number;
  color: [string, string];
}

/**
 * Sunflower layout: bead `i` at radius ~ sqrt(i), golden-angle turns, a touch of jitter.
 * Past `cap` beads they start piling on top of the first ones, which reads as a heap.
 */
export function beadSpot(
  slot: number,
  i: number,
  cap: number,
  spreadX = 36,
  spreadY = 32,
): BeadSpot {
  const layer = Math.floor(i / cap);
  const j = i % cap;
  const r = Math.sqrt((j + 0.5) / cap);
  const angle = j * 2.39996 + slot * 0.9 + layer * 0.7;
  const jx = (hash(slot, i) - 0.5) * 6;
  const jy = (hash(i, slot + 31) - 0.5) * 6;
  const color = BEAD_COLORS[Math.floor(hash(slot + 7, i * 3 + 1) * BEAD_COLORS.length)]!;
  return {
    x: 50 + Math.cos(angle) * r * spreadX + jx,
    y: 50 + Math.sin(angle) * r * spreadY + jy - layer * 3,
    color,
  };
}
